// Browser storage cleanup utilities for automatic cache and localStorage management

interface DeviceInfo {
  id: string;
  firstVisit: string;
  lastCleanup: string;
  userAgent: string;
  cleanupCount: number;
}

interface StorageQuota {
  used: number;
  total: number;
  percentage: number;
}

interface CleanupConfig {
  maxAge: number; // milliseconds
  environment: 'development' | 'production';
  autoCleanupInterval: number; // milliseconds
  storageThreshold: number; // percentage (0-100)
  retainKeys: string[]; // keys to never delete
}

const DEFAULT_CONFIG: CleanupConfig = {
  maxAge: 7 * 24 * 60 * 60 * 1000, // 7 days
  environment: import.meta.env.MODE === 'development' ? 'development' : 'production',
  autoCleanupInterval: 24 * 60 * 60 * 1000, // 24 hours
  storageThreshold: 80, // 80%
  retainKeys: [
    'auth.token',
    'user.preferences', 
    'app.settings',
    'device.info',
    'cleanup.config'
  ]
};

// Device detection and fingerprinting
export const generateDeviceId = (): string => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Device fingerprint', 2, 2);
  }
  
  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    screen.width + 'x' + screen.height,
    new Date().getTimezoneOffset(),
    canvas.toDataURL()
  ].join('|');
  
  // Simple hash function
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  return Math.abs(hash).toString(36);
};

// Get or create device info
export const getDeviceInfo = (): DeviceInfo => {
  const stored = localStorage.getItem('device.info');
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (error) {
      console.warn('Failed to parse device info:', error);
    }
  }
  
  const deviceInfo: DeviceInfo = {
    id: generateDeviceId(),
    firstVisit: new Date().toISOString(),
    lastCleanup: new Date().toISOString(),
    userAgent: navigator.userAgent,
    cleanupCount: 0
  };
  
  localStorage.setItem('device.info', JSON.stringify(deviceInfo));
  return deviceInfo;
};

// Check if this is a new device
export const isNewDevice = (): boolean => {
  const deviceInfo = getDeviceInfo();
  const daysSinceFirstVisit = (Date.now() - new Date(deviceInfo.firstVisit).getTime()) / (1000 * 60 * 60 * 24);
  return daysSinceFirstVisit < 1; // Less than 1 day old
};

// Get storage quota information
export const getStorageQuota = async (): Promise<StorageQuota> => {
  if ('storage' in navigator && 'estimate' in navigator.storage) {
    try {
      const estimate = await navigator.storage.estimate();
      const used = estimate.usage || 0;
      const total = estimate.quota || 0;
      const percentage = total > 0 ? Math.round((used / total) * 100) : 0;
      
      return { used, total, percentage };
    } catch (error) {
      console.warn('Failed to get storage estimate:', error);
    }
  }
  
  // Fallback estimation
  const used = JSON.stringify(localStorage).length;
  const total = 10 * 1024 * 1024; // Assume 10MB default
  return {
    used,
    total,
    percentage: Math.round((used / total) * 100)
  };
};

// Clean expired localStorage items
export const cleanExpiredLocalStorage = (config: Partial<CleanupConfig> = {}): number => {
  const cleanupConfig = { ...DEFAULT_CONFIG, ...config };
  const now = Date.now();
  let cleanedCount = 0;
  
  const keysToRemove: string[] = [];
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || cleanupConfig.retainKeys.includes(key)) continue;
    
    try {
      const item = localStorage.getItem(key);
      if (!item) continue;
      
      // Try to parse as timestamped data
      const parsed = JSON.parse(item);
      if (parsed && typeof parsed === 'object' && parsed.timestamp) {
        const age = now - new Date(parsed.timestamp).getTime();
        if (age > cleanupConfig.maxAge) {
          keysToRemove.push(key);
        }
      }
      // For items without timestamp, check if they're really old (heuristic)
      else if (key.includes('temp') || key.includes('cache') || key.includes('old')) {
        keysToRemove.push(key);
      }
    } catch (error) {
      // If parsing fails, it might be old format - remove if it looks temporary
      if (key.includes('temp') || key.includes('cache')) {
        keysToRemove.push(key);
      }
    }
  }
  
  // Remove expired items
  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
    cleanedCount++;
  });
  
  return cleanedCount;
};

// Clean browser caches
export const cleanBrowserCaches = async (): Promise<boolean> => {
  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      const now = Date.now();
      
      // Delete old caches (older than 7 days)
      const deletePromises = cacheNames
        .filter(cacheName => {
          // Extract version or timestamp from cache name
          const match = cacheName.match(/-v(\d+)$/) || cacheName.match(/-(\d{13})$/);
          if (match) {
            const timestamp = parseInt(match[1]);
            const age = now - timestamp;
            return age > (7 * 24 * 60 * 60 * 1000); // 7 days
          }
          return false;
        })
        .map(cacheName => caches.delete(cacheName));
      
      await Promise.all(deletePromises);
      console.log(`🧹 Cleaned ${deletePromises.length} old caches`);
      return true;
    } catch (error) {
      console.error('Failed to clean browser caches:', error);
      return false;
    }
  }
  return false;
};

// Priority-based cleanup when storage is full
export const performEmergencyCleanup = async (): Promise<number> => {
  console.log('🚨 Performing emergency storage cleanup...');
  
  let totalCleaned = 0;
  
  // 1. Clean expired localStorage items first
  totalCleaned += cleanExpiredLocalStorage();
  
  // 2. Clean browser caches
  await cleanBrowserCaches();
  
  // 3. Clean non-essential items if still over threshold
  const quota = await getStorageQuota();
  if (quota.percentage > 70) {
    const nonEssentialPrefixes = ['temp_', 'cache_', 'old_', 'backup_'];
    
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && nonEssentialPrefixes.some(prefix => key.startsWith(prefix))) {
        localStorage.removeItem(key);
        totalCleaned++;
      }
    }
  }
  
  console.log(`🧹 Emergency cleanup completed: ${totalCleaned} items removed`);
  return totalCleaned;
};

// Scheduled cleanup manager
class CleanupScheduler {
  private intervalId: number | null = null;
  private config: CleanupConfig;
  
  constructor(config: Partial<CleanupConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }
  
  start(): void {
    if (this.intervalId) {
      this.stop();
    }
    
    // Adjust interval based on environment
    const interval = this.config.environment === 'development' 
      ? 60 * 60 * 1000 // 1 hour in development
      : this.config.autoCleanupInterval; // 24 hours in production
    
    this.intervalId = window.setInterval(() => {
      this.performScheduledCleanup();
    }, interval);
    
    console.log(`🕐 Cleanup scheduler started (${this.config.environment}): every ${interval / 1000 / 60} minutes`);
  }
  
  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      console.log('⏹️ Cleanup scheduler stopped');
    }
  }
  
  private async performScheduledCleanup(): Promise<void> {
    console.log('🕐 Performing scheduled cleanup...');
    
    // Check storage quota first
    const quota = await getStorageQuota();
    
    if (quota.percentage > this.config.storageThreshold) {
      await performEmergencyCleanup();
    } else {
      // Normal cleanup
      const cleaned = cleanExpiredLocalStorage(this.config);
      console.log(`🧹 Scheduled cleanup: ${cleaned} items removed`);
    }
    
    // Update device info
    const deviceInfo = getDeviceInfo();
    deviceInfo.lastCleanup = new Date().toISOString();
    deviceInfo.cleanupCount++;
    localStorage.setItem('device.info', JSON.stringify(deviceInfo));
  }
}

// Main cleanup initialization
export const initializeBrowserStorageCleanup = async (config: Partial<CleanupConfig> = {}): Promise<void> => {
  console.log('🚀 Initializing browser storage cleanup...');
  
  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  const deviceInfo = getDeviceInfo();
  
  // Clean up on new device
  if (isNewDevice()) {
    console.log('🆕 New device detected, performing full cleanup...');
    await performEmergencyCleanup();
  }
  
  // Check if cleanup is needed
  const lastCleanup = new Date(deviceInfo.lastCleanup).getTime();
  const timeSinceCleanup = Date.now() - lastCleanup;
  
  if (timeSinceCleanup > finalConfig.autoCleanupInterval) {
    console.log('🧹 Cleanup overdue, performing immediate cleanup...');
    const cleaned = cleanExpiredLocalStorage(finalConfig);
    console.log(`Cleaned ${cleaned} expired items`);
  }
  
  // Start scheduled cleanup
  const scheduler = new CleanupScheduler(finalConfig);
  scheduler.start();
  
  // Monitor storage quota
  const quota = await getStorageQuota();
  console.log(`📊 Storage usage: ${quota.percentage}% (${(quota.used / 1024 / 1024).toFixed(2)}MB / ${(quota.total / 1024 / 1024).toFixed(2)}MB)`);
  
  if (quota.percentage > finalConfig.storageThreshold) {
    console.log('⚠️ Storage threshold exceeded, performing cleanup...');
    await performEmergencyCleanup();
  }
  
  // Expose cleanup functions globally for admin use
  if (typeof window !== 'undefined') {
    (window as any).forceCleanup = performEmergencyCleanup;
    (window as any).getStorageQuota = getStorageQuota;
    (window as any).cleanExpiredStorage = cleanExpiredLocalStorage;
  }
};

// Export the scheduler for advanced usage
export { CleanupScheduler };